import { atomFamily } from "jotai-family";
import { atomWithDefault } from "jotai/utils";
import { isEqual } from "lodash-es";
import { config } from "../../../config";
import { getTokenAtom } from "../../../server/auth/store";
import { authHeader } from "../../../utils/auth-header";
import type { BrandTone } from "../../brand-kit/server/brand-tone-service";
import { debugLabel } from "../../generate/store";
import type { AccountContext } from "../../generic/hooks/use-account-context";
import { trackContentGenerationResult } from "../../content-generation/track-content-generation";
import { contentGenerationIdAtom } from "./content-generation-id-atom";
import { selfDriving } from "../../self-driving/self-driving";

export const layoutConfigurationFamily = atomFamily(
  ({
    formatId,
    answer,
    imgNum,
    cacheBuster,
    fileId,
    brandTones,
    tone,
    context,
  }: {
    formatId: string;
    answer: string;
    imgNum: number;
    cacheBuster?: string;
    fileId?: string;
    brandTones: BrandTone[];
    tone?: string;
    context?: AccountContext;
  }) =>
    debugLabel(
      `layoutConfigurationFamily(${formatId}, ${answer}, ${imgNum}, ${cacheBuster})`,
      atomWithDefault(async (get) => {
        const { getToken } = get(getTokenAtom);
        const token = await getToken();

        if (!token) return;

        const selectedTone = brandTones.find((brand) => brand.id === tone);
        const contentGenerationId = get(contentGenerationIdAtom);

        const { data, error } = await selfDriving.POST("/multiformat/{formatId}/image/{imgNum}", {
          body: {
            description: answer,
            ...(fileId && { fileId }),
            ...(selectedTone ? { toneSummary: selectedTone?.summary } : {}),
            ...(context && {
              context: {
                companyName: context?.companyName ?? undefined,
                industry: context?.industry ?? undefined,
                companyDescription: context?.companyDescription ?? undefined,
                companyValues: context?.companyValues ?? undefined,
                companyTeamReference: context?.companyTeamReference ?? undefined,
              },
            }),
          },
          params: {
            path: { formatId, imgNum: imgNum.toString() },
            query: { model: config.gpt_model, cacheBuster },
          },
          ...authHeader(token),
        });

        if (error) throw error;

        let contentGenerationResultId = null;
        if (contentGenerationId && data) {
          contentGenerationResultId = await trackContentGenerationResult(
            token,
            contentGenerationId,
            data,
          );
        }

        return data ? { ...data, contentGenerationResultId } : data;
      }),
    ),

  isEqual,
);
